import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { type Passage, prefetchPassage } from "../api";
import { sharedAudio } from "../audio";
import { loadPrefs, savePrefs } from "../lib/prefs";
import {
  CHUNK_FONT,
  clipEnd,
  ECHO_GUARD_MS,
  ECHO_REVEAL_MS,
  echoGapMs,
  fitFontSize,
  frame,
  isSilent,
  keyAction,
  MODE_LABELS,
  MODES,
  type Mode,
  nextStep,
  type PlayerAction,
  stageRate,
  stepRate,
  tapZone,
} from "../lib/rsvp";
import { addVoice, emptyTally, gapFinished, isVoiced, voicedCount, voicedRatio } from "../lib/voice";
import { disableMic, enableMic, micLevel } from "../mic";
import { ChunkEditor } from "./ChunkEditor";
import { btnInk, btnShu } from "./Cram";

/**
 * playing / gap (Echo pause) / paused / transition (between passes) /
 * blocked (the browser refused to start without a tap) / done (end of collection).
 */
type Status = "playing" | "gap" | "paused" | "transition" | "blocked" | "done";

const STAGE_HINT: Record<Mode, string> = {
  listen: "聞く",
  echo: "止まったら、見ずに同じように言う",
  read: "音は出ません。同じペースで声に出して読む",
  finish: "少し速く。声に出して読み切る",
};

const STAGE_PAUSE_MS = 800;
const PASSAGE_PAUSE_MS = 1200;

export function RsvpPlayer({
  passage,
  onPass,
  onStart,
  onGo,
  onRestart,
  onClose,
  onEdited,
}: {
  passage: Passage & { audioUrl: string };
  onPass: (play: { mode: Mode; rate: number; voicedRatio?: number }) => void;
  onStart: (mode: Mode) => void;
  /** Move to another passage in the same collection. */
  onGo: (id: string) => void;
  /** Start the collection over from its first passage. */
  onRestart: () => void;
  onClose: () => void;
  onEdited: () => void;
}) {
  const { segments } = passage;
  const stageRef = useRef<HTMLDivElement>(null);
  const [prefs, setPrefs] = useState(loadPrefs);
  const [idx, setIdx] = useState(0);
  const [status, setStatus] = useState<Status>("playing");
  const [mode, setMode] = useState<Mode>("listen");
  const [upNext, setUpNext] = useState("");
  const [clip, setClip] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [fontPx, setFontPx] = useState(32);
  const [hidden, setHidden] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const r = useRef({
    cur: 0,
    echoed: -1,
    mode: "listen" as Mode,
    rate: prefs.rate,
    loop: prefs.loop,
    status: "playing" as Status,
    raf: 0,
    timer: 0 as ReturnType<typeof setTimeout> | 0,
    /** Deferred transition, run on the next play when paused mid-transition. */
    resume: null as (() => void) | null,
    clipPaused: false,
    clipAt: 0,
    inClip: false,
    /** The current stage's play is already logged (seeking back after the end must not log it twice). */
    logged: false,
    reveal: 0 as ReturnType<typeof setTimeout> | 0,
    gapThen: null as (() => void) | null,
    gapStart: 0,
    gapVoiced: 0,
    gapSilent: 0,
    tally: emptyTally(segments.length),
    micOn: false,
    speaking: false,
    vraf: 0,
    vlast: 0,
  }).current;
  r.micOn = micOn;
  r.rate = prefs.rate;
  r.loop = prefs.loop;

  const audio = sharedAudio;

  const set = (s: Status) => {
    r.status = s;
    setStatus(s);
  };

  const show = (k: number) => {
    if (k === r.cur) return;
    r.cur = k;
    setIdx(k);
  };

  const halt = () => {
    cancelAnimationFrame(r.raf);
    clearTimeout(r.timer);
    clearTimeout(r.reveal);
    r.timer = 0;
    r.gapThen = null;
    setHidden(false);
  };

  const enterGap = (k: number, then: () => void) => {
    halt();
    r.echoed = k;
    set("gap");
    Object.assign(r, { gapThen: then, gapStart: performance.now(), gapVoiced: 0, gapSilent: 0 });
    r.reveal = setTimeout(() => setHidden(true), ECHO_REVEAL_MS);
    r.timer = setTimeout(() => {
      r.gapThen = null;
      then();
    }, echoGapMs(segments[k], r.rate));
  };

  const updatePrefs = (patch: Partial<typeof prefs>) =>
    setPrefs((p) => {
      const next = { ...p, ...patch };
      savePrefs(next);
      return next;
    });

  const loop = () => {
    const a = audio();
    const f = frame(segments, a.currentTime * 1000, r);
    show(f.idx);
    if (f.pause === "echo") {
      a.pause();
      enterGap(f.idx, play);
    } else r.raf = requestAnimationFrame(loop);
  };

  function play() {
    halt();
    if (r.resume) {
      const run = r.resume;
      r.resume = null;
      return run();
    }
    set("playing");
    audio()
      .play()
      .catch(() => {
        halt();
        set("blocked");
      });
    r.raf = requestAnimationFrame(loop);
  }

  const pause = () => {
    halt();
    audio().pause();
    set("paused");
  };

  /** Restart the passage from its first chunk in the given stage. */
  const startStage = (m: Mode) => {
    halt();
    r.resume = null;
    r.mode = m;
    setMode(m);
    r.echoed = -1;
    r.logged = false;
    r.tally = emptyTally(segments.length);
    r.cur = 0;
    setIdx(0);
    const a = audio();
    a.muted = isSilent(m);
    a.defaultPlaybackRate = a.playbackRate = stageRate(m, r.rate);
    a.currentTime = 0;
    if (m === "read" && passage.nextId) prefetchPassage(passage.nextId);
    onStart(m);
    play();
  };

  const after = (label: string, ms: number, run: () => void) => {
    halt();
    setUpNext(label);
    set("transition");
    r.resume = run;
    r.timer = setTimeout(() => {
      r.resume = null;
      run();
    }, ms);
  };

  const passDone = () => {
    halt();
    const measured = r.micOn && r.mode !== "listen";
    if (!r.logged)
      onPass({
        mode: r.mode,
        rate: stageRate(r.mode, r.rate),
        ...(measured && { voicedRatio: voicedRatio(r.tally) }),
      });
    r.logged = true;
    const voice = measured ? `声 ${voicedCount(r.tally)}/${segments.length} ・ ` : "";
    setUpNext(voice);
    const step = nextStep(r.mode, r.loop, passage.nextId !== null);
    if (step.kind === "stage")
      after(`${voice}次: ${MODE_LABELS[step.mode]}`, STAGE_PAUSE_MS, () => startStage(step.mode));
    else if (step.kind === "loop") after(`${voice}もう一度 Listen から`, PASSAGE_PAUSE_MS, () => startStage("listen"));
    else if (step.kind === "next") after(`${voice}次のパッセージへ`, PASSAGE_PAUSE_MS, () => onGo(passage.nextId!));
    else set("done");
  };

  const seekTo = (k: number, andPlay: boolean) => {
    const target = Math.min(segments.length - 1, Math.max(0, k));
    halt();
    r.resume = null;
    r.echoed = -1;
    audio().currentTime = segments[target].fromMs / 1000;
    show(target);
    if (andPlay) play();
    else set("paused");
  };

  const endClip = () => {
    cancelAnimationFrame(r.raf);
    const a = audio();
    a.pause();
    setClip(null);
    if (!r.inClip) return;
    r.inClip = false;
    a.muted = isSilent(r.mode);
    a.currentTime = r.clipAt;
    if (r.clipPaused) play();
  };

  const playClip = (k: number) => {
    if (!r.inClip) {
      r.clipPaused = r.status === "playing" || r.status === "gap" || r.status === "transition";
      if (r.clipPaused) pause();
      r.clipAt = audio().currentTime;
      r.inClip = true;
    }
    const a = audio();
    const last = clipEnd(k, segments.length);
    cancelAnimationFrame(r.raf);
    a.muted = false;
    a.currentTime = segments[k].fromMs / 1000;
    setClip(k);
    a.play().catch(endClip);
    let cur = k;
    const tick = () => {
      const ms = a.currentTime * 1000;
      if (ms >= segments[last].toMs - ECHO_GUARD_MS || a.ended) return endClip();
      while (cur < last && ms >= segments[cur].toMs) setClip(++cur);
      r.raf = requestAnimationFrame(tick);
    };
    r.raf = requestAnimationFrame(tick);
  };

  const startEdit = () => {
    if (r.status === "playing" || r.status === "gap" || r.status === "transition") pause();
    setEditing(true);
  };

  const act = (action: PlayerAction) => {
    const running = r.status === "playing" || r.status === "gap" || r.status === "transition";
    if (r.inClip) endClip();
    if (r.status === "blocked" && action !== "close") {
      startStage(r.mode);
      return;
    }
    switch (action) {
      case "toggle":
        if (running) pause();
        else if (r.status === "done") startStage("listen");
        else play();
        break;
      case "prev":
        seekTo(r.cur - 1, running);
        break;
      case "next":
        seekTo(r.cur + 1, running);
        break;
      case "listen":
      case "echo":
      case "read":
      case "finish":
        startStage(action);
        break;
      case "faster":
        updatePrefs({ rate: stepRate(r.rate, 1) });
        break;
      case "slower":
        updatePrefs({ rate: stepRate(r.rate, -1) });
        break;
      case "loop":
        updatePrefs({ loop: !r.loop });
        break;
      case "mic":
        toggleMic();
        break;
      case "prevPassage":
        if (passage.prevId) onGo(passage.prevId);
        break;
      case "nextPassage":
        if (passage.nextId) onGo(passage.nextId);
        break;
      case "close":
        onClose();
        break;
    }
  };

  const toggleMic = async () => {
    if (r.micOn) {
      disableMic();
      setMicOn(false);
      updatePrefs({ mic: false });
      return;
    }
    const ok = await enableMic();
    setMicOn(ok);
    updatePrefs({ mic: ok });
  };

  const sample = (t: number) => {
    const level = micLevel();
    const dt = Math.min(100, t - r.vlast);
    r.vlast = t;
    if (level !== null) {
      const voiced = isVoiced(level);
      if (voiced !== r.speaking) {
        r.speaking = voiced;
        setSpeaking(voiced);
      }
      if (r.status === "gap") {
        if (voiced) {
          addVoice(r.tally, r.echoed, dt);
          r.gapVoiced += dt;
          r.gapSilent = 0;
        } else r.gapSilent += dt;
        const then = r.gapThen;
        if (then && gapFinished({ elapsedMs: t - r.gapStart, voicedMs: r.gapVoiced, silentMs: r.gapSilent })) {
          halt();
          then();
        }
      } else if (r.status === "playing" && isSilent(r.mode) && voiced) addVoice(r.tally, r.cur, dt);
    }
    r.vraf = requestAnimationFrame(sample);
  };

  useEffect(() => {
    if (!micOn) return;
    r.vlast = performance.now();
    r.vraf = requestAnimationFrame(sample);
    return () => {
      cancelAnimationFrame(r.vraf);
      r.speaking = false;
      setSpeaking(false);
    };
  }, [micOn]);

  const actRef = useRef(act);
  actRef.current = act;

  useEffect(() => {
    const a = audio();
    const onEnded = () => {
      if (r.inClip) return;
      const last = segments.length - 1;
      show(last);
      if (r.mode === "echo" && r.echoed !== last) enterGap(last, passDone);
      else passDone();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const action = keyAction(e.key, e.shiftKey);
      if (!action) return;
      e.preventDefault();
      actRef.current(action);
    };
    a.addEventListener("ended", onEnded);
    window.addEventListener("keydown", onKey);
    a.defaultPlaybackRate = r.rate;
    a.playbackRate = r.rate;
    a.src = passage.audioUrl;
    startStage("listen");
    if (loadPrefs().mic) enableMic().then((ok) => (ok ? setMicOn(true) : updatePrefs({ mic: false })));
    return () => {
      halt();
      a.removeEventListener("ended", onEnded);
      window.removeEventListener("keydown", onKey);
      a.pause();
    };
  }, []);

  useEffect(() => {
    audio().defaultPlaybackRate = audio().playbackRate = stageRate(mode, prefs.rate);
  }, [prefs.rate, mode]);

  useLayoutEffect(() => {
    const ctx = document.createElement("canvas").getContext("2d");
    const measure = () => {
      if (!ctx || !stageRef.current) return;
      ctx.font = `500 40px ${CHUNK_FONT}`;
      const widest = Math.max(0, ...segments.map((s) => ctx.measureText(s.text).width));
      setFontPx(fitFontSize(widest, stageRef.current.clientWidth));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [segments]);

  const onStageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (r.status === "blocked") return act("toggle");
    const rect = e.currentTarget.getBoundingClientRect();
    act(tapZone((e.clientX - rect.left) / rect.width));
  };

  const running = status === "playing" || status === "gap" || status === "transition";
  const mark = speaking ? "w-1.5 bg-shu" : status === "gap" ? "w-1 bg-shu" : "w-0.5 bg-ink-3/50";

  return (
    <div
      onKeyUp={(e) => e.key === " " && e.preventDefault()}
      className="rsvp-touch fixed inset-0 flex flex-col bg-sheet text-ink select-none"
    >
      <div className="flex items-center justify-between gap-3 border-b-2 border-ink bg-paper px-3 py-2">
        <button
          type="button"
          onClick={onClose}
          aria-label="ホームへ (Esc)"
          title="ホームへ (Esc)"
          className="grid h-9 w-9 shrink-0 place-items-center border-2 border-ink bg-sheet text-lg leading-none font-bold hover:bg-marker"
        >
          ×
        </button>
        <div className="min-w-0 truncate text-center text-[12px] font-bold">
          {passage.collection.title}
          {passage.title && <span className="text-ink-2"> ・ {passage.title}</span>}
        </div>
        <span className="w-9 shrink-0" aria-hidden />
      </div>

      <div className="flex flex-1 flex-col items-center justify-center px-4">
        <div className={`h-7 ${mark}`} />
        <div className="w-full max-w-3xl border-y border-rule">
          <div
            ref={stageRef}
            onClick={onStageClick}
            className="flex w-full cursor-pointer items-center justify-center py-3"
            style={{ height: fontPx * 1.25 * 2 + 24 }}
          >
            {status === "blocked" ? (
              <span className={btnShu}>▶ タップで開始</span>
            ) : (
              <div
                aria-live="off"
                className="text-center font-medium text-ink"
                style={{
                  fontFamily: CHUNK_FONT,
                  fontSize: fontPx,
                  lineHeight: 1.25,
                  visibility: hidden && status === "gap" ? "hidden" : "visible",
                }}
              >
                {segments[idx]?.text}
              </div>
            )}
          </div>
        </div>
        <div className={`h-7 ${mark}`} />
        <div className="mt-5 flex min-h-11 items-center justify-center text-[12px] font-bold">
          {status === "gap" ? (
            <span className="bg-shu px-2 py-0.5 tracking-widest text-white">あなたの番</span>
          ) : status === "done" ? (
            <span className="flex flex-wrap items-center justify-center gap-3">
              {upNext && <span className="w-full text-center text-shu">{upNext.replace(/ ・ $/, "")}</span>}
              <button type="button" onClick={() => startStage("listen")} className={btnShu}>
                もう一度
              </button>
              <button type="button" onClick={onRestart} className={btnInk}>
                最初のパッセージから
              </button>
            </span>
          ) : (
            <span className={status === "transition" ? "text-shu" : "text-ink-3"}>
              {status === "transition" ? upNext : status === "paused" ? "一時停止中" : status === "playing" ? STAGE_HINT[mode] : ""}
            </span>
          )}
        </div>
        <section
          className="mt-3 max-h-[30vh] w-full max-w-3xl overflow-y-auto border-t border-rule pt-3"
          style={{ visibility: status === "gap" ? "hidden" : "visible" }}
        >
          {editing ? (
            <>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="float-right text-[12px] font-bold text-ink-2 underline underline-offset-4 hover:text-shu"
              >
                やめる
              </button>
              <ChunkEditor id={passage.id} chunks={passage.chunks} onSaved={onEdited} />
            </>
          ) : (
            <>
              {passage.owned && (
                <button
                  type="button"
                  onClick={startEdit}
                  className="float-right ml-3 text-[12px] font-bold text-ink-2 underline underline-offset-4 hover:text-shu"
                >
                  区切りを直す
                </button>
              )}
              <p className="text-[15px] leading-loose text-ink-2">
                {segments.map((s, k) => (
                  <Fragment key={k}>
                    {k > 0 && <span className="px-1 font-bold text-shu/60">/</span>}
                    <button
                      type="button"
                      onClick={() => playClip(k)}
                      title="ここから 3 チャンクを再生"
                      className={`px-0.5 hover:bg-marker/50 ${clip === k ? "marker font-bold text-ink" : idx === k ? "text-ink underline decoration-ink-3 underline-offset-4" : ""}`}
                    >
                      {s.text}
                    </button>
                  </Fragment>
                ))}
              </p>
              {passage.translation && (
                <p className="mt-2 font-mincho text-[13px] leading-7 text-shu">{passage.translation}</p>
              )}
              {passage.note && <p className="mt-1 font-mincho text-[12px] leading-6 text-ink-3">{passage.note}</p>}
            </>
          )}
        </section>
      </div>

      <div className="h-1.5 w-full border-t border-rule">
        {status !== "playing" && status !== "gap" && (
          <div className="h-full bg-shu" style={{ width: `${((idx + 1) / segments.length) * 100}%` }} />
        )}
      </div>

      <div className="flex flex-col items-center gap-2 border-t-2 border-ink bg-paper px-3 py-3">
        <div className="flex flex-wrap items-center justify-center gap-2">
          <div className="flex h-9 border-2 border-ink bg-sheet text-[12px] font-black">
            {MODES.map((m, k) => (
              <button
                key={m}
                type="button"
                onClick={() => act(m)}
                title={`${MODE_LABELS[m]} から (${k + 1})`}
                aria-pressed={m === mode}
                className={`border-l border-ink px-2.5 first:border-l-0 ${m === mode ? "bg-ink text-marker" : "hover:bg-marker"}`}
              >
                {k + 1} {MODE_LABELS[m]}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => act("loop")}
            aria-pressed={prefs.loop}
            title="このパッセージをくり返す (L)"
            className={`h-9 border-2 border-ink px-2.5 text-[12px] font-black ${prefs.loop ? "bg-ink text-marker" : "bg-sheet hover:bg-marker"}`}
          >
            ↻ くり返す
          </button>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Ctl label="前のパッセージ (Shift+←)" onClick={() => act("prevPassage")} disabled={!passage.prevId}>
            ⏮
          </Ctl>
          <Ctl label="前のチャンク (←)" onClick={() => act("prev")}>
            ‹
          </Ctl>
          <Ctl label="再生 / 一時停止 (Space)" onClick={() => act("toggle")} primary>
            {running ? "❚❚" : "▶"}
          </Ctl>
          <Ctl label="次のチャンク (→)" onClick={() => act("next")}>
            ›
          </Ctl>
          <Ctl label="次のパッセージ (Shift+→)" onClick={() => act("nextPassage")} disabled={!passage.nextId}>
            ⏭
          </Ctl>
          <div className="flex h-10 items-stretch border-2 border-ink bg-sheet text-[14px] font-bold">
            <button type="button" onClick={() => act("slower")} title="遅く (↓)" className="px-3 hover:bg-marker">
              −
            </button>
            <span className="grid w-16 place-items-center bg-ink font-mono text-[13px] text-marker tabular-nums">
              ×{stageRate(mode, prefs.rate).toFixed(2)}
            </span>
            <button type="button" onClick={() => act("faster")} title="速く (↑)" className="px-3 hover:bg-marker">
              +
            </button>
          </div>
          <button
            type="button"
            onClick={() => act("mic")}
            aria-pressed={micOn}
            title={micOn ? "発声チェック ON:録音も送信もしません (M)" : "発声チェックを ON にする (M)"}
            className={`h-10 border-2 border-ink px-2.5 text-[12px] font-black ${micOn ? "bg-ink text-marker" : "bg-sheet hover:bg-marker"}`}
          >
            {micOn ? "● 声" : "○ 声"}
          </button>
        </div>
      </div>

    </div>
  );
}

function Ctl({
  label,
  onClick,
  primary,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={`grid h-10 w-10 place-items-center border-2 border-ink text-[15px] font-bold disabled:opacity-30 ${
        primary ? "bg-shu text-white hover:bg-shu-deep" : "bg-sheet hover:bg-marker"
      }`}
    >
      {children}
    </button>
  );
}
